<script lang="ts">
	import MaterialDetailsEditView from './MaterialDetailsEditView.svelte'
	import MaterialCatalogs from './MaterialCatalogs.svelte'
	import { onMount, setContext, untrack } from 'svelte'
	import { writable } from 'svelte/store'
	import { fly } from 'svelte/transition'
	import { t } from '$i18n/i18n'
	import { fetchUtils } from '$utils/fetchUtils'
	import { hideAnimation, showAnimation } from '$lib/stores'
	import Button from '$lib/atoms/Button.svelte'
	import IconPlus from '$svg/icon-plus.svg?component'
	import { InputIcons, LoadingAnimation } from '$types/enums'
	import { modals } from 'svelte-modals'
	import MaterialCatalogModal from './MaterialCatalogModal.svelte'
	import type { Material } from './types/material'
	import { EmptyMaterial } from './types/material'
	import { MaterialView } from './types/materialView'
	import { DefaultMaterialProperty } from './types/materialProperty'
	import MaterialProperties from './MaterialProperties.svelte'
	import MaterialPropertyModal from './MaterialPropertyModal.svelte'
	import { changeUrl } from '$utils/misc'
	import { inputFieldFormats } from '$utils/formatters'
	import TabMenu from '$lib/organisms/TabMenu.svelte'
	import MaterialSubstitutes from './MaterialSubstitutes.svelte'
	import Basic from '$lib/templates/Basic.svelte'
	import InputField from '$lib/molecules/InputField.svelte'
	import MaterialSubstituteModal from './MaterialSubstituteModal.svelte'
	import type { SubstituteType } from './types/substitutes'
	import { mmpvState } from '$lib/mmpv/mmpv.state.svelte'
	import { fetchCatalogs, addCatalogToState } from './mmpv-catalogs.controller.svelte'
	import {
		fetchProperties,
		updateNodePropertiesByDeletingProperty
	} from '$lib/mmpv/mmpv-properties.controller.svelte'
	import { checkScripts } from '$lib/mmpv/mmpv-substitutes.controller.svelte'
	import QuantitiesPage from '$lib/mmpv/QuantitiesPage.svelte'
	import QuantitiesModal from '$lib/mmpv/QuantitiesModal.svelte'
	import { DefaultQuantityUnit } from '$lib/mmpv/types/quantity'
	import { fetchQuantities } from '$lib/mmpv/mmpv-quantities.controller.svelte'

	// We must set the formErrors context here, even though it does not necessarily need to be assigned to a variable!
	setContext('formErrors', writable([{ path: '', message: '' }]))

	const CATALOGS_PAGE = 'catalogs'
	const MATERIALS_PAGE = 'catalog-materials'
	const PROPERTIES_PAGE = 'properties'
	const SUBSTITUTES_PAGE = 'substitutes'
	const QUANTITIES_PAGE = 'quantities'

	interface Props {
		catalogName?: string
		materialId?: string
		materialPropertyId?: string
		view?: MaterialView
	}

	let {
		catalogName: initialCatalogName,
		materialId,
		materialPropertyId,
		view: initialView = MaterialView.CATALOGS
	}: Props = $props()

	let view: MaterialView = $state(untrack(() => initialView))
	let catalogName: string | undefined = $state(untrack(() => initialCatalogName))

	let buttonLabel: string = $derived.by(() => {
		switch (view) {
			case MaterialView.CATALOG_MATERIALS:
				return $t('UI.mmpv.createMaterial')
			case MaterialView.CATALOGS:
				return $t('UI.mmpv.createCatalog')
			case MaterialView.MATERIAL_PROPERTIES:
				return $t('UI.mmpv.createProperty')
			case MaterialView.MATERIAL_SUBSTITUTES:
				return $t('UI.mmpv.createSubstitute')
			case MaterialView.QUANTITIES:
				return $t('UI.mmpv.createQuantity')
		}
	})

	const tabs = $derived([
		{ label: $t('UI.mmpv.catalogsList.tab'), id: CATALOGS_PAGE },
		{
			label: $t('UI.mmpv.catalog.tab'),
			id: MATERIALS_PAGE,
			disabled: mmpvState.catalogs.length === 0
		},
		{ label: $t('UI.mmpv.properties.tab'), id: PROPERTIES_PAGE },
		{ label: $t('UI.mmpv.substitutes.tab'), id: SUBSTITUTES_PAGE },
		{ label: $t('UI.mmpv.quantities.tab'), id: QUANTITIES_PAGE }
	])

	const viewToTabId: Record<MaterialView, string> = {
		[MaterialView.CATALOGS]: CATALOGS_PAGE,
		[MaterialView.CATALOG_MATERIALS]: MATERIALS_PAGE,
		[MaterialView.MATERIAL_PROPERTIES]: PROPERTIES_PAGE,
		[MaterialView.MATERIAL_SUBSTITUTES]: SUBSTITUTES_PAGE,
		[MaterialView.QUANTITIES]: QUANTITIES_PAGE
	}

	function handleTabChange(index: number) {
		const mainEl = document.querySelector<HTMLElement>('main')
		if (mainEl) mainEl.scrollTop = 0
		switch (tabs[index].id) {
			case CATALOGS_PAGE:
				navigateCatalogs()
				break
			case MATERIALS_PAGE:
				navigateCatalogMaterials()
				break
			case PROPERTIES_PAGE:
				navigateProperties()
				break
			case SUBSTITUTES_PAGE:
				navigateSubstitutes()
				break
			case QUANTITIES_PAGE:
				navigateQuantities()
				break
		}
	}

	let materialEdit: MaterialDetailsEditView = $state() // MaterialDetailsEditView component

	let showFilters: boolean = false

	let selectedSubstituteType: SubstituteType | undefined = $state()

	onMount(async () => {
		await fetchData()

		if (materialId === 'new') {
			navigateCatalogs() // used to handle reload page with new unsaved material
		}

		if (catalogName && catalogName !== '') {
			// in case we visit via the url /catalog/
			let cat = mmpvState.catalogs.find((c) => c.name === catalogName)
			mmpvState.selectedCatalog = cat ?? (mmpvState.catalogs.length > 0 ? mmpvState.catalogs[0] : undefined)
			if (materialId && materialId !== 'new' && cat) {
				let nodes = cat.nodes
				if (nodes !== null && nodes.length !== 0) {
					mmpvState.selectedMaterial = nodes.find((m) => m.id === materialId) ?? EmptyMaterial()
					let mat: Material | undefined = mmpvState.selectedMaterial
					const catalogKey = mmpvState.selectedCatalog.name
					mmpvState.expandTreeNode(catalogKey, mat.id)
					let parId: string | undefined = mat.parentId
					while (parId && parId !== 'root') {
						mat = nodes.find((m) => m.id === parId)
						// TODO fix undefined case here
						parId = mat?.parentId
						if (mat) {
							mmpvState.expandTreeNode(catalogKey, mat.id)
						}
					}
					view = MaterialView.CATALOG_MATERIALS
				}
			}
		} else if (materialPropertyId && materialPropertyId !== '') {
			// we need to open the modal with the given property
			await modals.open(MaterialPropertyModal, {
				materialProperty: {
					...DefaultMaterialProperty(),
					...mmpvState.getMaterialProperty(materialPropertyId)
				},
				isNew: false,
				parent: null
			})
		}
	})

	async function fetchData() {
		showAnimation(LoadingAnimation.MmLoader)
		await Promise.all([fetchCatalogs(), fetchProperties(), fetchQuantities()])
			.catch(fetchUtils.catchErrorAndShowNotification)
			.finally(() => hideAnimation(800))
	}

	function handleCreateCatalog() {
		modals.open(MaterialCatalogModal, {
			update: addCatalogToState
		})
	}

	function handleCreateProperty() {
		modals.open(MaterialPropertyModal, {
			materialProperty: DefaultMaterialProperty(),
			isNew: true,
			parent: null
		})
	}

	function handleCreateSubstitute() {
		if (!selectedSubstituteType) return
		modals.open(MaterialSubstituteModal, {
			substituteType: selectedSubstituteType,
			catalogs: $state.snapshot(mmpvState.catalogs)
		})
	}

	function handleCreateQuantity() {
		modals.open(QuantitiesModal, {
			quantityUnit: DefaultQuantityUnit(),
			isNew: true
		})
	}

	function createNewItem() {
		// resetSearchAndFilters();
		switch (view) {
			case MaterialView.CATALOG_MATERIALS:
				materialEdit.addRoot() // function // in MaterialEditView
				break
			case MaterialView.CATALOGS:
				handleCreateCatalog()
				break
			case MaterialView.MATERIAL_PROPERTIES:
				handleCreateProperty()
				break
			case MaterialView.MATERIAL_SUBSTITUTES:
				handleCreateSubstitute()
				break
			case MaterialView.QUANTITIES:
				handleCreateQuantity()
				break
		}
	}

	// TODO maybe also for tab navigation we should check whether we have unsaved data and show modal then?
	function navigateCatalogs() {
		view = MaterialView.CATALOGS
		changeUrl('/catalogs', window)
		mmpvState.resetSearchAndFilters()
	}

	function navigateCatalogMaterials() {
		if (mmpvState.selectedCatalog?.nodes) {
			handleNavigateCatalog()
		}
		view = MaterialView.CATALOG_MATERIALS
		mmpvState.resetSearchAndFilters()
	}

	// in case we visit via /catalogs select
	function handleNavigateCatalog() {
		if (
			!mmpvState.selectedCatalog ||
			!mmpvState.selectedCatalog?.nodes ||
			mmpvState.selectedCatalog?.nodes.length === 0
		) {
			mmpvState.selectedMaterial = EmptyMaterial()
			return
		}
		catalogName = mmpvState.selectedCatalog.name

		if (!mmpvState.selectedMaterial) return

		// select first material if last selected material doesn't match actual catalog
		if (
			mmpvState.selectedMaterial.id === 'new' ||
			!mmpvState.selectedCatalog?.nodes
				.map((node) => node.id)
				.includes(mmpvState.selectedMaterial.id)
		) {
			mmpvState.selectedMaterial = (mmpvState.selectedCatalog?.nodes ?? []).reduce((min, node) =>
				(node.parentCode ?? '') + node.code < (min.parentCode ?? '') + min.code ? node : min
			)
		}
		changeUrl(`catalog/${catalogName}/material/${mmpvState.selectedMaterial.id}`, window)
	}

	function navigateProperties() {
		view = MaterialView.MATERIAL_PROPERTIES
		changeUrl('/materialProperties', window)
		mmpvState.resetSearchAndFilters()
	}

	function navigateSubstitutes() {
		view = MaterialView.MATERIAL_SUBSTITUTES
		changeUrl('/materialSubstitutes', window)
		mmpvState.resetSearchAndFilters()
	}

	function navigateQuantities() {
		view = MaterialView.QUANTITIES
		changeUrl('/quantities', window)
		mmpvState.resetSearchAndFilters()
	}

	function search() {
		mmpvState.filters = []
		if (mmpvState.searchTerm) {
			switch (view) {
				case MaterialView.CATALOGS:
					mmpvState.searchCatalogs()
					break
				case MaterialView.MATERIAL_PROPERTIES:
					mmpvState.searchProperties()
					break
				case MaterialView.CATALOG_MATERIALS:
					mmpvState.searchMaterials()
					break
				case MaterialView.MATERIAL_SUBSTITUTES:
					// nothing to do yet
					break
				case MaterialView.QUANTITIES:
					mmpvState.searchQuantities()
					break
			}
		}
	}
</script>

<Basic>
	<div class="main-container">
		<section class="filters" style="height: 100%">
			<!-- filter list todo possibly own component with context store -->
			<span>{$t('UI.mmpv.materialCatalog')}</span>
			<div class="filter-row">
				<div>
					<!-- TODO: Implement-->
					<!--<SecondaryButton label={$t('UI.filters')}
                                     color={showFilters ? SecondaryBtnColors.GREEN : SecondaryBtnColors.GREY}
                                     rightIcon={showFilters ? ButtonIcons.UP_ARROW : ButtonIcons.DOWN_ARROW}
                                     on:click={() => showFilters = !showFilters} flushTextAndIcon/>
                    <SecondaryButton label={$t('UI.deleteAll')} color={SecondaryBtnColors.RED}
                                     on:click={() => selectedFilters = []} sizeAdaptingToText/>-->
				</div>
				<div>
					{#if view === MaterialView.MATERIAL_SUBSTITUTES}
						<Button variant="primary" onclick={checkScripts}>
							<span class="create-button-label">{$t('UI.mmpv.checkScripts')}</span>
						</Button>
					{/if}
					<Button variant="primary" titleAttr={buttonLabel} onclick={createNewItem}>
						<IconPlus width={20} height={20} fill="inherit" stroke-width={2} />
						<span class="create-button-label">{buttonLabel}</span>
					</Button>
					<InputField
						bind:value={mmpvState.searchTerm}
						placeholder={$t('UI.placeholder.search')}
						format={inputFieldFormats.SEARCH}
						icon={InputIcons.SEARCH_LENS}
						on:input={search}
					/>
				</div>
			</div>
			{#if showFilters}
				<div class="filter-row">FILTER AUSWAHL TBD</div>
			{/if}
		</section>
		<nav>
			<!-- TABS for navigation -->
			<TabMenu
				{tabs}
				activeIndex={tabs.findIndex((tab) => tab.id === viewToTabId[view])}
				onChange={handleTabChange}
				justify="start"
			/>
		</nav>
		<section class="main-content">
			<div class="view-wrapper">
				<!-- content with the views of catalogs list or material list for one catalog -->
				<!-- {#each [view] as currentView} freezes currentView per iteration so the old block
				     keeps rendering its original content during the out transition, preventing an
				     immediate height collapse when switching from a tall view to a short one. -->
				{#each [view] as currentView (currentView)}
					<div
						style="margin-top:3rem"
						in:fly={{ x: 60, opacity: 0, duration: 220 }}
						out:fly={{ x: -60, opacity: 0, duration: 220 }}
					>
						{#if currentView === MaterialView.CATALOG_MATERIALS}
							<MaterialDetailsEditView bind:this={materialEdit} backToCatalogs={navigateCatalogs} />
						{:else if currentView === MaterialView.CATALOGS}
							<!--no need to update material or catalog, is done via context store -->
							<MaterialCatalogs onCatalogSelected={navigateCatalogMaterials} />
						{:else if currentView === MaterialView.MATERIAL_PROPERTIES}
							<MaterialProperties onUpdateProperties={updateNodePropertiesByDeletingProperty} />
						{:else if currentView === MaterialView.MATERIAL_SUBSTITUTES}
							<MaterialSubstitutes bind:substituteType={selectedSubstituteType} />
						{:else if currentView === MaterialView.QUANTITIES}
							<QuantitiesPage />
						{/if}
					</div>
				{/each}
			</div>
		</section>
	</div>
</Basic>

<style>
	@import '$styles/global.scss';

	nav {
		background: white;
		border-radius: 10px 10px 0 0;
		border-bottom: 1px solid var(--color-slate-300);
	}

	.create-button-label {
		white-space: nowrap;
		font-size: inherit;
		font-weight: inherit;
		line-height: inherit;
		color: inherit;
	}

	.main-content {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;

		flex-grow: 1;
		user-select: none;
	}

	.view-wrapper {
		display: grid;
		width: 100%;
		overflow: hidden;
		align-self: flex-start;
		align-items: start;
	}

	.view-wrapper > div {
		grid-column: 1;
		grid-row: 1;
	}

	section.filters {
		display: flex;
		flex-direction: column;
		gap: 30px;
		justify-content: flex-start;
		align-items: flex-start;
		margin-bottom: 2.5rem;

		& > span {
			font-family: Roboto, sans-serif;
			line-height: normal;
			font-size: 1.25rem;
			color: black;
			font-weight: 500;
		}

		& .filter-row {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			align-items: center;
			gap: 14px;

			width: 100%;

			& > div {
				display: flex;
				flex-direction: row;
				justify-content: center;
				align-items: center;
				gap: 6px;

				&:first-child {
					justify-content: flex-start;
				}
			}
		}
	}
</style>
